import { useTranslation } from 'react-i18next';

import { useHelpHotjarUrl } from '@hotjar/translations';
import { SmartPanelSegment } from './SmartPanelBlocks';
import { SmartPanelSpacedText } from './SmartPanelSpacedText';
import type { TrackableSite } from '../../constants-types/ExternalDependencyTypes';
import type { AggregatePageInsightsComponentProps } from '../../constants-types/AggregatePageInsights';
import { useGetPageDropOffRate } from '../../hooks/useGetPageDropOffRate';

const READ_MORE_LINK = '/articles/11276556755735#exit_rate';

interface DropOffRateSegmentProps extends AggregatePageInsightsComponentProps {
  currentSite?: TrackableSite;
}

type DropOffRateProps = Required<DropOffRateSegmentProps>;

function DropOffRate({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  currentSite,
  isDisabled,
  disabledTooltipText,
}: DropOffRateProps): JSX.Element | null {
  const { t } = useTranslation('heatmaps');
  const helpHotjarUrl = useHelpHotjarUrl();

  const isFeatureAllowed =
    Boolean(currentSite.effectivePlan) && currentSite.effectivePlan !== 'basic';

  const rate = useGetPageDropOffRate({
    selectedDateRange,
    selectedTargetingValue,
    selectedDeviceType,
    searchPattern,
    siteId: currentSite.id,
    enabled: !isDisabled,
  });

  const rateText = rate !== undefined ? `${rate}%` : '-';

  const onReadMoreClick = () => {
    window.open(`${helpHotjarUrl}${READ_MORE_LINK}`, '_blank', 'noopener noreferrer');
  };

  if (!isFeatureAllowed) return null;

  return (
    <SmartPanelSegment data-testid="hj-hm-sp-drop-off-rate">
      <SmartPanelSpacedText
        leftText={`${t('smart-panel.drop-off-rate')}`}
        rightText={rateText}
        leftIcon="flow"
        isDisabled={isDisabled}
        disabledTooltipText={disabledTooltipText}
        popoverProps={{
          title: t('smart-panel.drop-off-rate'),
          text: t('smart-panel.drop-off-rate-help'),
          actionText: t('smart-panel.read-more'),
          onActionPress: onReadMoreClick,
        }}
      />
    </SmartPanelSegment>
  );
}

export function DropOffRateSegment({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  currentSite,
  isDisabled,
  disabledTooltipText,
}: DropOffRateSegmentProps): JSX.Element | null {
  if (currentSite && currentSite.id) {
    return (
      <DropOffRate
        selectedDateRange={selectedDateRange}
        selectedTargetingValue={selectedTargetingValue}
        selectedDeviceType={selectedDeviceType}
        searchPattern={searchPattern}
        currentSite={currentSite}
        isDisabled={isDisabled}
        disabledTooltipText={disabledTooltipText}
      />
    );
  }

  return null;
}
